import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Assessment,
} from '@mui/icons-material';
import { ru } from 'date-fns/locale';
import { reportsApi, holdersApi } from '../services/api';
import { formatDate, formatCurrency } from '../utils/formatters';
import { useApp } from '../App';

const Reports = () => {
  const { showNotification } = useApp();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reports, setReports] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [holders, setHolders] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Report parameters
  const [params, setParams] = useState({
    type: 'portfolio',
    date: new Date(),
    holders: [],
    format: 'xlsx',
    detail: 'detailed',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportsRes, typesRes, holdersRes] = await Promise.all([
        reportsApi.getAll(),
        reportsApi.getTypes(),
        holdersApi.getAll(),
      ]);

      setReports(reportsRes.data);
      setReportTypes(typesRes.data);
      setHolders(holdersRes.data);
    } catch (error) {
      showNotification('Ошибка загрузки данных', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      let result;

      if (params.type === 'portfolio') {
        result = await reportsApi.generatePortfolio({
          date: params.date.toISOString().split('T')[0],
          holders: params.holders,
          format: params.format,
          detail: params.detail,
        });
      } else if (params.type === 'expenses') {
        result = await reportsApi.generateExpenses({
          startDate: params.startDate?.toISOString().split('T')[0],
          endDate: params.endDate?.toISOString().split('T')[0],
          holders: params.holders,
        });
      }

      showNotification('Отчет сформирован успешно', 'success');
      setPreviewData(result.data);
      setPreviewOpen(true);
      fetchData();
    } catch (error) {
      showNotification('Ошибка формирования отчета', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (id) => {
    try {
      const response = await reportsApi.download(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      showNotification('Ошибка скачивания отчета', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить отчет?')) return;

    try {
      await reportsApi.delete(id);
      showNotification('Отчет удален', 'success');
      fetchData();
    } catch (error) {
      showNotification('Ошибка удаления', 'error');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight={600}>
            Отчеты
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Report Generator */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Формирование отчета
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Тип отчета</InputLabel>
                    <Select
                      value={params.type}
                      label="Тип отчета"
                      onChange={(e) => setParams({ ...params, type: e.target.value })}
                    >
                      {reportTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <DatePicker
                    label="Дата среза"
                    value={params.date}
                    onChange={(date) => setParams({ ...params, date })}
                    slotProps={{ textField: { fullWidth: true } }}
                  />

                  <FormControl fullWidth>
                    <InputLabel>Владельцы</InputLabel>
                    <Select
                      multiple
                      value={params.holders}
                      onChange={(e) => setParams({ ...params, holders: e.target.value })}
                      input={<OutlinedInput label="Владельцы" />}
                      renderValue={(selected) => selected.join(', ') || 'Все'}
                    >
                      {holders.map((h) => (
                        <MenuItem key={h.id} value={h.code}>
                          <Checkbox checked={params.holders.includes(h.code)} />
                          <ListItemText primary={h.code} secondary={h.name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Детализация</InputLabel>
                    <Select
                      value={params.detail}
                      label="Детализация"
                      onChange={(e) => setParams({ ...params, detail: e.target.value })}
                    >
                      <MenuItem value="summary">Сводный</MenuItem>
                      <MenuItem value="detailed">Детальный</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>Формат</InputLabel>
                    <Select
                      value={params.format}
                      label="Формат"
                      onChange={(e) => setParams({ ...params, format: e.target.value })}
                    >
                      <MenuItem value="xlsx">Excel (.xlsx)</MenuItem>
                      <MenuItem value="pdf">PDF</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="contained"
                    size="large"
                    startIcon={generating ? <CircularProgress size={20} /> : <Assessment />}
                    onClick={handleGenerateReport}
                    disabled={generating}
                    fullWidth
                  >
                    {generating ? 'Формирование...' : 'Сформировать отчет'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Reports History */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  История отчетов
                </Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Дата</TableCell>
                        <TableCell>Название</TableCell>
                        <TableCell>Тип</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell width={150}>Действия</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : reports.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            Нет сформированных отчетов
                          </TableCell>
                        </TableRow>
                      ) : (
                        reports.map((report) => (
                          <TableRow key={report.id}>
                            <TableCell>{formatDate(report.createdAt, 'dd.MM.yyyy HH:mm')}</TableCell>
                            <TableCell>{report.name}</TableCell>
                            <TableCell>
                              {reportTypes.find((t) => t.id === report.type)?.name || report.type}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={report.status}
                                size="small"
                                color={
                                  report.status === 'COMPLETED'
                                    ? 'success'
                                    : report.status === 'GENERATING'
                                    ? 'warning'
                                    : 'error'
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownload(report.id)}
                                disabled={report.status !== 'COMPLETED'}
                              >
                                Скачать
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<DeleteIcon />}
                                onClick={() => handleDelete(report.id)}
                              >
                                Удалить
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Preview Dialog */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Отчет сформирован</DialogTitle>
          <DialogContent>
            {previewData && (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Отчет "{previewData.report?.name}" успешно сформирован
                </Alert>
                <Typography variant="subtitle1" gutterBottom>
                  Сводка:
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Total NAV</TableCell>
                      <TableCell align="right">
                        {formatCurrency(previewData.summary?.totalNAV, 'USD')}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Cost Basis</TableCell>
                      <TableCell align="right">
                        {formatCurrency(previewData.summary?.totalCostBasis, 'USD')}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>P&L</TableCell>
                      <TableCell align="right">
                        {formatCurrency(previewData.summary?.totalPL, 'USD')}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Позиций</TableCell>
                      <TableCell align="right">{previewData.summary?.positionsCount}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>Закрыть</Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(previewData?.report?.id)}
            >
              Скачать
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default Reports;
